import { useEffect, useRef, useState } from 'preact/hooks';
import { Terminal as XTerm } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Play, Square, Settings, RotateCcw, Terminal as TerminalIcon } from 'lucide-preact';
import 'xterm/css/xterm.css';

interface TerminalProps {
  onQuery: (query: string, sessionId?: string) => Promise<any>;
  session?: {
    sessionId: string;
    permissionMode: string;
    model: string;
    cwd: string;
  };
}

export default function Terminal({ onQuery, session }: TerminalProps) {
  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<XTerm | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [vimMode, setVimMode] = useState(false);
  const [notificationEnabled, setNotificationEnabled] = useState(true);

  // Notification function
  const sendNotification = (title: string, body: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body });
    } else if ('Notification' in window && Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification(title, { body });
        }
      });
    }
  };

  useEffect(() => {
    if (!terminalRef.current) return;

    // Initialize xterm.js terminal with optimized settings
    const terminal = new XTerm({
      theme: {
        background: '#0a0a0a',
        foreground: '#ffffff',
        cursor: '#ffffff',
        selectionBackground: '#ffffff40',
        black: '#000000',
        red: '#ff5555',
        green: '#50fa7b',
        yellow: '#f1fa8c',
        blue: '#8be9fd',
        magenta: '#ff79c6',
        cyan: '#8be9fd',
        white: '#f8f8f2',
        brightBlack: '#6272a4',
        brightRed: '#ff6e6e',
        brightGreen: '#69ff94',
        brightYellow: '#ffffa5',
        brightBlue: '#d6acff',
        brightMagenta: '#ff92df',
        brightCyan: '#a4ffff',
        brightWhite: '#ffffff',
      },
      fontSize: 14,
      fontFamily: 'Monaco, Menlo, "Ubuntu Mono", "Cascadia Code", "JetBrains Mono", monospace',
      cursorBlink: true,
      cursorStyle: 'block',
      scrollback: 1000,
      tabStopWidth: 4,
      allowTransparency: false,
      convertEol: true,
      disableStdin: false,
    });

    // Add addons
    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();
    terminal.loadAddon(fitAddon);
    terminal.loadAddon(webLinksAddon);

    // Open terminal
    terminal.open(terminalRef.current);
    fitAddon.fit();

    // Store reference
    xtermRef.current = terminal;

    // Handle terminal bell events
    terminal.onBell(() => {
      if (notificationEnabled) {
        sendNotification('Terminal Bell', 'Bell character received');
      }
    });

    // Connect to WebSocket terminal
    const connectToTerminal = () => {
      const ws = new WebSocket('ws://localhost:8787/terminal');
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Connected to terminal WebSocket');
        setIsConnected(true);
        
        // Initialize terminal with current size
        const { cols, rows } = fitAddon.proposeDimensions();
        ws.send(JSON.stringify({
          type: 'init',
          cols: cols || 80,
          rows: rows || 24
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'output') {
            terminal.write(data.data);
          } else if (data.type === 'exit') {
            terminal.writeln('');
            terminal.writeln(`Process exited with code ${data.code}`);
            terminal.write('> ');
            
            // Send notification if enabled
            if (notificationEnabled) {
              sendNotification('Task completed', `Process exited with code ${data.code}`);
            }
          } else if (data.type === 'error') {
            terminal.writeln('');
            terminal.writeln(`Error: ${data.message}`);
            terminal.write('> ');
            
            // Send notification for errors
            if (notificationEnabled) {
              sendNotification('Task failed', data.message);
            }
          } else if (data.type === 'bell') {
            // Terminal bell notification
            if (notificationEnabled) {
              // Trigger bell sound/notification
              sendNotification('Terminal Bell', 'Bell notification received');
            }
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        console.log('Terminal WebSocket disconnected');
        setIsConnected(false);
        terminal.writeln('');
        terminal.writeln('Connection lost. Reconnecting...');
        
        // Reconnect after 3 seconds
        setTimeout(connectToTerminal, 3100);
      };

      ws.onerror = (error) => {
        console.error('Terminal WebSocket error:', error);
        setIsConnected(false);
      };
    };

    // Handle terminal input with line break support
    terminal.onData((data) => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        // Handle special key combinations
        const charCode = data.charCodeAt(0);
        
        // Shift+Enter (line break)
        if (charCode === 13 && data.length > 1) {
          wsRef.current.send(JSON.stringify({
            type: 'input',
            data: '\\\n' // Quick escape for line break
          }));
          return;
        }
        
        // Option+Enter (line break for Mac)
        if (charCode === 13 && data.includes('\u001b')) {
          wsRef.current.send(JSON.stringify({
            type: 'input',
            data: '\\\n' // Quick escape for line break
          }));
          return;
        }
        
        // Handle terminal configuration commands
        if (data === '/config\n') {
          terminal.writeln('\r\nByte DevOps Terminal Configuration:');
          terminal.writeln('• Vim Mode: ' + (vimMode ? 'Enabled' : 'Disabled') + ' (use /vim to toggle)');
          terminal.writeln('• Notifications: ' + (notificationEnabled ? 'Enabled' : 'Disabled') + ' (use /notifications to toggle)');
          terminal.writeln('• Line Breaks: Shift+Enter or Option+Enter for newlines');
          terminal.writeln('• Terminal Bell: Enabled for task completion');
          terminal.writeln('• Font: Monaco, Menlo, Ubuntu Mono, Cascadia Code, JetBrains Mono');
          terminal.writeln('• Theme: Dark with syntax highlighting');
          terminal.write('> ');
          return;
        }
        
        if (data === '/vim\n') {
          setVimMode(!vimMode);
          terminal.writeln('\r\nVim Mode: ' + (!vimMode ? 'Enabled' : 'Disabled'));
          terminal.write('> ');
          return;
        }
        
        if (data === '/notifications\n') {
          setNotificationEnabled(!notificationEnabled);
          terminal.writeln('\r\nNotifications: ' + (!notificationEnabled ? 'Enabled' : 'Disabled'));
          terminal.write('> ');
          return;
        }
        
        if (data === '/terminal-setup\n') {
          terminal.writeln('\r\nTerminal Setup Instructions:');
          terminal.writeln('1. Enable Shift+Enter for line breaks');
          terminal.writeln('2. Enable Option+Enter for line breaks (Mac)');
          terminal.writeln('3. Configure terminal bell notifications');
          terminal.writeln('4. Set up proper font and theme');
          terminal.writeln('5. Enable notification permissions in browser');
          terminal.write('> ');
          return;
        }
        
        if (data === '/statusline\n') {
          terminal.writeln('\r\nStatus Line Configuration:');
          terminal.writeln('• Project info: Shows current project and type');
          terminal.writeln('• Git branch: Displays current branch and changes');
          terminal.writeln('• Terminal status: Connection indicator');
          terminal.writeln('• Auth status: Authentication state');
          terminal.writeln('• Metrics: Cost, duration, lines changed');
          terminal.writeln('• Counts: Todos and subagents');
          terminal.writeln('• View: Current project view');
          terminal.writeln('• Fixed at bottom: Always visible');
          terminal.write('> ');
          return;
        }
        
        // Regular input
        wsRef.current.send(JSON.stringify({
          type: 'input',
          data: data
        }));
      }
    });

    // Handle window resize
    const handleResize = () => {
      fitAddon.fit();
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        const { cols, rows } = fitAddon.proposeDimensions();
        wsRef.current.send(JSON.stringify({
          type: 'resize',
          cols: cols || 80,
          rows: rows || 24
        }));
      }
    };
    window.addEventListener('resize', handleResize);

    // Connect to terminal
    connectToTerminal();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (wsRef.current) {
        wsRef.current.close();
      }
      terminal.dispose();
    };
  }, []);

  const handleSend = async () => {
    // This is now handled by the terminal's onData event
    // The input field is just for display purposes
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  return (
    <div className="flex flex-1 flex-col">
      <div className="flex items-center justify-between p-2 bg-muted border-b">
        <div className="flex items-center gap-2 text-sm">
          <TerminalIcon className="h-4 w-4" />
          Byte DevOps Terminal
          {isConnected ? (
            <span className="text-green-500 text-xs">● Connected</span>
          ) : (
            <span className="text-red-500 text-xs">● Disconnected</span>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>Vim: {vimMode ? 'ON' : 'OFF'}</span>
          <span>Notifications: {notificationEnabled ? 'ON' : 'OFF'}</span>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            <RotateCcw className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <div ref={terminalRef} className="flex-1 bg-black min-h-0" />
      <div className="border-t p-2 bg-muted/50">
        <div className="text-xs text-muted-foreground flex items-center gap-4">
          <span><strong>Real Terminal:</strong> Actual Byte DevOps CLI</span>
          <span><strong>Auth:</strong> <code>/login</code></span>
          <span><strong>Config:</strong> <code>/config</code></span>
          <span><strong>Setup:</strong> <code>/terminal-setup</code></span>
          <span><strong>Status:</strong> <code>/statusline</code></span>
          <span><strong>Line Breaks:</strong> Shift+Enter or Option+Enter</span>
        </div>
      </div>
    </div>
  );
}